import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import PracticeOptionsModal from '../components/vocabulary/PracticeOptionsModal';
import { ClickableText } from '../components/ClickableWord';
import { convertPhonetic, getUserPhoneticSystem } from '../utils/phoneticConverter';
import './VocabularyBookPage.css';

import { API_BASE_URL } from '../config';
import { resolveMediaUrl } from '../utils/mediaUrl';

// ========== SVG Icons ==========
const Icons = {
  Book: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  Search: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  X: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  ),
  Play: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  Stop: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  ),
  Star: ({ filled }: { filled?: boolean }) => (
    <svg viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" width="20" height="20">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Trash: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),
  Note: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  ChevronDown: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Check: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  CheckSquare: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  BarChart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  Practice: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h2v2H7zm0 4h2v2H7zm0 4h2v2H7zm4-8h6v2h-6zm0 4h6v2h-6zm0 4h6v2h-6z" />
    </svg>
  ),
  AlertCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="64" height="64">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  BookOpen: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="200" height="200">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  ExternalLink: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
};

// ========== Interfaces ==========
interface Vocabulary {
  id: string;
  word: string;
  pronunciation?: string;
  definition?: string;
  examples?: Array<{
    sentence: string;
    translation: string;
  }>;
}

// Dictionary example format (from AI generation)
interface DictionaryExample {
  pos: string;
  meaning: string;
  example_en: string;
  example_native: string;
}

interface UserVocabulary {
  id: string;
  userId: string;
  vocabularyId: string;
  masteryLevel: number;
  isFavorite: boolean;
  notes?: string;
  reviewCount: number;
  lastReviewedAt?: string;
  createdAt: string;
  vocabulary: Vocabulary;
}

type SortOption = 'date-desc' | 'date-asc' | 'alpha-asc' | 'alpha-desc' | 'mastery-asc' | 'mastery-desc';

// ========== Constants ==========
const MASTERY_COLORS = ['#9CA3AF', '#EF4444', '#F97316', '#EAB308', '#84CC16', '#22C55E'];
const REVIEW_INTERVAL_DAYS = [0, 1, 3, 7, 14, 30]; // Days until next review based on mastery

// ========== Helper Functions ==========
const getMasteryColor = (level: number): string => MASTERY_COLORS[level] || '#9CA3AF';

const isDueForReview = (lastReviewedAt?: string, masteryLevel?: number): boolean => {
  if (!lastReviewedAt) return true;
  const lastReview = new Date(lastReviewedAt);
  const now = new Date();
  const daysSinceReview = Math.floor((now.getTime() - lastReview.getTime()) / (1000 * 60 * 60 * 24));
  const intervalDays = REVIEW_INTERVAL_DAYS[masteryLevel || 0] || 1;
  return daysSinceReview >= intervalDays;
};

// formatDate is now inside the component to access t() and i18n

// ========== Components ==========

// Skeleton Card Component
const SkeletonCard: React.FC = () => (
  <div className="vocab-skeleton-card" aria-hidden="true">
    <div className="vocab-skeleton-header">
      <div>
        <div className="vocab-skeleton-word" />
        <div className="vocab-skeleton-phonetic" />
      </div>
      <div className="vocab-skeleton-actions">
        <div className="vocab-skeleton-btn" />
        <div className="vocab-skeleton-btn" />
      </div>
    </div>
    <div className="vocab-skeleton-definition" />
    <div className="vocab-skeleton-definition" style={{ width: '70%' }} />
    <div className="vocab-skeleton-footer">
      <div className="vocab-skeleton-ring" />
      <div className="vocab-skeleton-date" />
    </div>
  </div>
);

// Progress Ring Component
interface ProgressRingProps {
  level: number;
  size?: number;
  strokeWidth?: number;
  onClick?: () => void;
  ariaLabel?: string;
}

const ProgressRing: React.FC<ProgressRingProps> = ({ level, size = 52, strokeWidth = 4, onClick, ariaLabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = (level / 5) * 100;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className="mastery-ring-container"
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      tabIndex={0}
      role="button"
      aria-label={ariaLabel}
    >
      <svg className="mastery-ring" width={size} height={size}>
        <circle
          className="mastery-ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
        />
        <circle
          className="mastery-ring-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset,
            stroke: getMasteryColor(level),
          }}
        />
      </svg>
      <span className="mastery-ring-text" style={{ color: getMasteryColor(level) }}>
        {level}/5
      </span>
    </div>
  );
};

// ========== Main Component ==========
const VocabularyBookPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation('vocabulary');

  // Helper function to get mastery label from i18n
  const getMasteryLabel = useCallback((level: number): string => {
    return t(`mastery.level${level}`, t('mastery.unknown'));
  }, [t]);

  // Format date with i18n
  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('date.today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('date.yesterday');
    } else {
      return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    }
  }, [t, i18n.language]);
  const [vocabularyList, setVocabularyList] = useState<UserVocabulary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMastery, setFilterMastery] = useState<number | null>(null);
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('date-desc');

  // UI States
  const [batchMode, setBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [masteryDropdownId, setMasteryDropdownId] = useState<string | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);

  // Dictionary Examples States (AI-generated examples from dictionary API)
  const [dictionaryExamples, setDictionaryExamples] = useState<Record<string, DictionaryExample[]>>({});
  const [loadingExamples, setLoadingExamples] = useState<Set<string>>(new Set());

  // Modal States
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<UserVocabulary | null>(null);
  const [practiceModalOpen, setPracticeModalOpen] = useState(false);

  // Auto-open practice modal when ?practice=true
  // Keep vocabIds/kpIds in URL so onStart can forward them
  useEffect(() => {
    if (searchParams.get('practice') === 'true') {
      setPracticeModalOpen(true);
      searchParams.delete('practice');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Audio States
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup audio and timeout on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    };
  }, []);

  // Get user's phonetic system preference
  const phoneticSystem = useMemo(() => getUserPhoneticSystem(), []);

  // ========== Data Loading ==========
  const loadVocabularyBook = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError(t('loginRequired'));
        setLoading(false);
        return;
      }

      // Pass user's language preference to get localized definitions
      const locale = i18n.language || 'zh-TW';
      const response = await fetch(`${API_BASE_URL}/api/v1/vocabulary-book/me?locale=${encodeURIComponent(locale)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load vocabulary book');
      }

      const data = await response.json();
      setVocabularyList(data.data || []);
    } catch (err) {
      console.error('Failed to load vocabulary book:', err);
      setError(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t]);

  useEffect(() => {
    loadVocabularyBook();
  }, [loadVocabularyBook]);

  // ========== Filtering and Sorting ==========
  const filteredAndSortedList = useMemo(() => {
    let filtered = [...vocabularyList];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.vocabulary.word.toLowerCase().includes(query) ||
        item.vocabulary.definition?.toLowerCase().includes(query) ||
        item.notes?.toLowerCase().includes(query)
      );
    }

    // Mastery filter
    if (filterMastery !== null) {
      filtered = filtered.filter(item => item.masteryLevel === filterMastery);
    }

    // Favorite filter
    if (filterFavorite) {
      filtered = filtered.filter(item => item.isFavorite);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'date-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'alpha-asc':
          return a.vocabulary.word.localeCompare(b.vocabulary.word);
        case 'alpha-desc':
          return b.vocabulary.word.localeCompare(a.vocabulary.word);
        case 'mastery-asc':
          return a.masteryLevel - b.masteryLevel;
        case 'mastery-desc':
          return b.masteryLevel - a.masteryLevel;
        default:
          return 0;
      }
    });

    return filtered;
  }, [vocabularyList, searchQuery, filterMastery, filterFavorite, sortOption]);

  // ========== Statistics ==========
  const statistics = useMemo(() => {
    const distribution = [0, 0, 0, 0, 0, 0];
    let dueForReviewCount = 0;

    vocabularyList.forEach(item => {
      distribution[item.masteryLevel]++;
      if (isDueForReview(item.lastReviewedAt, item.masteryLevel)) {
        dueForReviewCount++;
      }
    });

    const maxCount = Math.max(...distribution, 1);

    return { distribution, maxCount, dueForReviewCount };
  }, [vocabularyList]);

  // ========== Action Handlers ==========
  const handleToggleFavorite = useCallback(async (item: UserVocabulary, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/vocabulary-book/${item.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ masteryLevel: item.masteryLevel, isFavorite: !item.isFavorite }),
      });

      if (response.ok) {
        setVocabularyList(prev => prev.map(v =>
          v.id === item.id ? { ...v, isFavorite: !v.isFavorite } : v
        ));
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  }, []);

  const handleUpdateMastery = useCallback(async (item: UserVocabulary, newLevel: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/vocabulary-book/${item.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ masteryLevel: newLevel }),
      });

      if (response.ok) {
        setVocabularyList(prev => prev.map(v =>
          v.id === item.id ? { ...v, masteryLevel: newLevel } : v
        ));
      }
    } catch (err) {
      console.error('Failed to update mastery:', err);
    }
    setMasteryDropdownId(null);
  }, []);

  const handleDeleteClick = useCallback((item: UserVocabulary, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setItemToDelete(item);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/v1/vocabulary-book/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        setVocabularyList(prev => prev.filter(v => v.id !== itemToDelete.id));
      }
    } catch (err) {
      console.error('Failed to delete vocabulary:', err);
    } finally {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  }, [itemToDelete]);

  // Batch Operations with Promise.allSettled for partial success handling
  const handleBatchDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map(id =>
          fetch(`${API_BASE_URL}/api/v1/vocabulary-book/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          }).then(res => {
            if (!res.ok) throw new Error(`Failed to delete ${id}`);
            return id;
          })
        )
      );

      // Filter out successful deletions
      const successfulIds = new Set(
        results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map(r => r.value)
      );

      if (successfulIds.size > 0) {
        setVocabularyList(prev => prev.filter(v => !successfulIds.has(v.id)));
      }

      // Log failures
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.error(`Failed to delete ${failures.length} items`);
      }

      setSelectedIds(new Set());
      setBatchMode(false);
    } catch (err) {
      console.error('Failed to batch delete:', err);
    }
  }, [selectedIds]);

  const handleBatchFavorite = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map(id =>
          fetch(`${API_BASE_URL}/api/v1/vocabulary-book/${id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ isFavorite: true }),
          }).then(res => {
            if (!res.ok) throw new Error(`Failed to favorite ${id}`);
            return id;
          })
        )
      );

      // Filter out successful favorites
      const successfulIds = new Set(
        results
          .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
          .map(r => r.value)
      );

      if (successfulIds.size > 0) {
        setVocabularyList(prev => prev.map(v =>
          successfulIds.has(v.id) ? { ...v, isFavorite: true } : v
        ));
      }

      // Log failures
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.error(`Failed to favorite ${failures.length} items`);
      }

      setSelectedIds(new Set());
      setBatchMode(false);
    } catch (err) {
      console.error('Failed to batch favorite:', err);
    }
  }, [selectedIds]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Fetch dictionary examples for a word
  const fetchDictionaryExamples = useCallback(async (word: string) => {
    // Skip if already loaded or loading
    if (dictionaryExamples[word] || loadingExamples.has(word)) return;

    setLoadingExamples(prev => new Set(prev).add(word));

    try {
      // Use user's language preference for localized examples
      const locale = i18n.language || 'zh-TW';
      const response = await fetch(
        `${API_BASE_URL}/dictionary/lookup/${encodeURIComponent(word)}?examples=true&locale=${encodeURIComponent(locale)}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.examples && data.examples.length > 0) {
          setDictionaryExamples(prev => ({ ...prev, [word]: data.examples }));
        } else {
          // Mark as empty array to prevent re-fetching
          setDictionaryExamples(prev => ({ ...prev, [word]: [] }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch dictionary examples:', err);
      // Mark as empty to prevent infinite retries
      setDictionaryExamples(prev => ({ ...prev, [word]: [] }));
    } finally {
      setLoadingExamples(prev => {
        const newSet = new Set(prev);
        newSet.delete(word);
        return newSet;
      });
    }
  }, [dictionaryExamples, loadingExamples, i18n.language]);

  const toggleExpand = useCallback((id: string, word: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        // Fetch dictionary examples when expanding
        fetchDictionaryExamples(word);
      }
      return newSet;
    });
  }, [fetchDictionaryExamples]);

  // Audio — reuse Audio element to avoid mobile autoplay restrictions
  const playPronunciation = useCallback(async (word: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Clear any existing timeout
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
    }

    setPlayingAudio(word);

    try {
      const response = await fetch(`${API_BASE_URL}/dictionary/lookup/${encodeURIComponent(word)}?audio=true`);
      if (response.ok) {
        const data = await response.json();
        if (data.audio && data.audio.length > 0) {
          const audioUrl = data.audio[0].url;
          if (audioUrl) {
            if (!audioRef.current) {
              audioRef.current = new Audio();
            }
            audioRef.current.src = resolveMediaUrl(audioUrl);
            audioRef.current.onended = () => setPlayingAudio(null);
            audioRef.current.onerror = () => setPlayingAudio(null);
            try {
              await audioRef.current.play();
            } catch {
              // Interrupted by a new play/pause call — safe to ignore
            }
            return;
          }
        }
      }
      setPlayingAudio(null);
    } catch (err) {
      console.error('Failed to play pronunciation:', err);
      setPlayingAudio(null);
    }
  }, []);

  // Clear Filters
  const clearFilters = useCallback(() => {
    setFilterMastery(null);
    setFilterFavorite(false);
    setSearchQuery('');
  }, []);

  // ========== Render ==========
  if (loading) {
    return (
      <div className="vocab-book-page">
        <div className="vocab-loading">
          <div className="vocab-loading-spinner" />
          <span className="vocab-loading-text">{t('loading')}</span>
        </div>
        <div className="vocab-list">
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vocab-book-page">
        <div className="vocab-error">
          <Icons.AlertCircle />
          <p>{error}</p>
          <button onClick={loadVocabularyBook}>{t('retry')}</button>
        </div>
      </div>
    );
  }

  const hasActiveFilters = filterMastery !== null || filterFavorite || searchQuery;

  return (
    <div className="vocab-book-page">
      {/* Header */}
      <div className="vocab-header">
        <div className="vocab-header-left">
          <h1>
            <Icons.Book />
            {t('title')}
          </h1>
          <div className="vocab-stats">
            <span className="stat-item">
              <strong>{vocabularyList.length}</strong> {t('stats.words')}
            </span>
            <span className="stat-item">
              <strong>{vocabularyList.filter(v => v.isFavorite).length}</strong> {t('stats.favorites')}
            </span>
            {statistics.dueForReviewCount > 0 && (
              <span className="stat-item" style={{ color: '#F59E0B' }}>
                <strong>{statistics.dueForReviewCount}</strong> {t('stats.dueForReview')}
              </span>
            )}
          </div>
        </div>
        {vocabularyList.length > 0 && (
          <div className="vocab-header-actions">
            <button
              className={`vocab-batch-toggle ${batchMode ? 'active' : ''}`}
              onClick={() => {
                setBatchMode(!batchMode);
                setSelectedIds(new Set());
              }}
            >
              <Icons.CheckSquare />
              {t('batch.toggle')}
            </button>
            <button
              className="vocab-practice-btn"
              onClick={() => setPracticeModalOpen(true)}
            >
              <Icons.Practice />
              {t('actions.practice')}
            </button>
          </div>
        )}
      </div>

      {/* Statistics Chart */}
      {vocabularyList.length > 0 && showStatistics && (
        <div className="vocab-statistics">
          <div className="vocab-statistics-header">
            <h3>
              <Icons.BarChart />
              {t('distribution.title')}
            </h3>
            <button
              className="vocab-statistics-toggle"
              onClick={() => setShowStatistics(false)}
              aria-label={t('distribution.hide')}
            >
              <Icons.X />
            </button>
          </div>
          <div className="mastery-distribution">
            {statistics.distribution.map((count, level) => (
              <div key={level} className="mastery-bar-item">
                <span className="mastery-bar-count">{count}</span>
                <div className="mastery-bar-wrapper">
                  <div
                    className="mastery-bar"
                    style={{
                      height: `${(count / statistics.maxCount) * 100}%`,
                      backgroundColor: getMasteryColor(level),
                    }}
                  />
                </div>
                <span className="mastery-bar-label">{getMasteryLabel(level)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="vocab-filters">
        <div className="vocab-filters-row">
          {/* Search Bar */}
          <div className="vocab-search-container">
            <label htmlFor="vocab-search" className="vocab-search-label">
              {t('search.label')}
            </label>
            <div className="vocab-search-wrapper">
              <Icons.Search className="vocab-search-icon" />
              <input
                id="vocab-search"
                type="text"
                placeholder={t('search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="vocab-search-input"
              />
              {searchQuery && (
                <button
                  className="vocab-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label={t('search.clear')}
                >
                  <Icons.X />
                </button>
              )}
            </div>
          </div>

          {/* Sort Dropdown */}
          <div className="vocab-sort-container">
            <label htmlFor="vocab-sort" className="vocab-search-label">
              {t('sort.label')}
            </label>
            <select
              id="vocab-sort"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              className="vocab-sort-select"
            >
              <option value="date-desc">{t('sort.dateDesc')}</option>
              <option value="date-asc">{t('sort.dateAsc')}</option>
              <option value="alpha-asc">{t('sort.alphaAsc')}</option>
              <option value="alpha-desc">{t('sort.alphaDesc')}</option>
              <option value="mastery-asc">{t('sort.masteryAsc')}</option>
              <option value="mastery-desc">{t('sort.masteryDesc')}</option>
            </select>
            <Icons.ChevronDown className="vocab-sort-icon" />
          </div>
        </div>

        {/* Filter Chips */}
        <div className="vocab-filter-chips">
          <button
            className={`vocab-filter-chip ${filterFavorite ? 'active' : ''}`}
            onClick={() => setFilterFavorite(!filterFavorite)}
          >
            <Icons.Star filled={filterFavorite} />
            {t('filter.favorite')}
          </button>

          {[0, 1, 2, 3, 4, 5].map(level => (
            <button
              key={level}
              className={`vocab-filter-chip ${filterMastery === level ? 'active' : ''}`}
              onClick={() => setFilterMastery(filterMastery === level ? null : level)}
              style={filterMastery === level ? {
                backgroundColor: getMasteryColor(level),
                borderColor: getMasteryColor(level)
              } : {}}
            >
              {getMasteryLabel(level)}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              className="vocab-filter-chip vocab-filter-chip--clear"
              onClick={clearFilters}
            >
              <Icons.X />
              {t('filter.clear')}
            </button>
          )}
        </div>
      </div>

      {/* Batch Actions Bar */}
      {batchMode && selectedIds.size > 0 && (
        <div className="vocab-batch-actions" role="status" aria-live="polite">
          <span className="vocab-batch-count">{t('batch.selected', { count: selectedIds.size })}</span>
          <div className="vocab-batch-buttons">
            <button className="vocab-batch-btn" onClick={handleBatchFavorite}>
              <Icons.Star filled />
              {t('batch.addFavorite')}
            </button>
            <button className="vocab-batch-btn vocab-batch-btn--danger" onClick={handleBatchDelete}>
              <Icons.Trash />
              {t('batch.delete')}
            </button>
          </div>
        </div>
      )}

      {/* Vocabulary List */}
      <div className="vocab-list">
        {filteredAndSortedList.length === 0 ? (
          <div className="vocab-empty">
            <div className="vocab-empty-illustration">
              <Icons.BookOpen />
            </div>
            {hasActiveFilters ? (
              <>
                <h3>{t('empty.noMatch')}</h3>
                <p>{t('empty.noMatchHint')}</p>
                <button className="vocab-empty-btn" onClick={clearFilters}>
                  {t('filter.clear')}
                </button>
              </>
            ) : (
              <>
                <h3>{t('empty.noWords')}</h3>
                <p>{t('empty.noWordsHint')}</p>
                <Link to="/dictionary" className="vocab-empty-btn">
                  <Icons.Search />
                  {t('empty.goToDictionary')}
                </Link>
              </>
            )}
          </div>
        ) : (
          filteredAndSortedList.map(item => {
            const isSelected = selectedIds.has(item.id);
            const isExpanded = expandedIds.has(item.id);
            const isDue = isDueForReview(item.lastReviewedAt, item.masteryLevel);

            return (
              <div
                key={item.id}
                className={`vocab-card ${batchMode ? 'batch-mode' : ''} ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => batchMode ? toggleSelect(item.id) : toggleExpand(item.id, item.vocabulary.word)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    batchMode ? toggleSelect(item.id) : toggleExpand(item.id, item.vocabulary.word);
                  }
                }}
                aria-pressed={batchMode ? isSelected : undefined}
                aria-expanded={!batchMode ? isExpanded : undefined}
              >
                {/* Batch Checkbox */}
                {batchMode && (
                  <div
                    className={`vocab-card-checkbox ${isSelected ? 'checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                  >
                    <Icons.Check />
                  </div>
                )}

                {/* Due for Review Badge */}
                {isDue && !batchMode && (
                  <div className="vocab-due-badge">
                    <Icons.Clock />
                    {t('card.dueForReview')}
                  </div>
                )}

                {/* Card Header */}
                <div className="vocab-card__header">
                  <div className="vocab-word-section">
                    <h3 className="vocab-word">{item.vocabulary.word}</h3>
                    {item.vocabulary.pronunciation && (
                      <span className="vocab-phonetic">
                        /{convertPhonetic(item.vocabulary.pronunciation, phoneticSystem)}/
                      </span>
                    )}
                    <button
                      className={`vocab-play-btn ${playingAudio === item.vocabulary.word ? 'playing' : ''}`}
                      onClick={(e) => playPronunciation(item.vocabulary.word, e)}
                      aria-label={playingAudio === item.vocabulary.word ? t('actions.stopAudio') : t('actions.playAudio')}
                    >
                      {playingAudio === item.vocabulary.word ? <Icons.Stop /> : <Icons.Play />}
                    </button>
                  </div>
                  <div className="vocab-actions">
                    <button
                      className={`vocab-action-btn ${item.isFavorite ? 'active' : ''}`}
                      onClick={(e) => handleToggleFavorite(item, e)}
                      aria-label={item.isFavorite ? t('actions.removeFavorite') : t('actions.toggleFavorite')}
                    >
                      <Icons.Star filled={item.isFavorite} />
                    </button>
                    <button
                      className="vocab-action-btn vocab-action-btn--delete"
                      onClick={(e) => handleDeleteClick(item, e)}
                      aria-label={t('actions.deleteWord')}
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                </div>

                {/* Card Content */}
                <div className="vocab-card__content">
                  <p className="vocab-definition">{item.vocabulary.definition || t('card.noDefinition')}</p>
                  {item.notes && (
                    <div className="vocab-notes">
                      <Icons.Note />
                      {item.notes}
                    </div>
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="vocab-expanded-content">
                    {(() => {
                      const word = item.vocabulary.word;
                      const dictExamples = dictionaryExamples[word];
                      const isLoading = loadingExamples.has(word);

                      if (isLoading) {
                        return (
                          <div className="vocab-examples">
                            <h4 className="vocab-examples-title">{t('examples.title')}</h4>
                            <p className="vocab-no-examples">{t('examples.loading')}</p>
                          </div>
                        );
                      }

                      if (dictExamples && dictExamples.length > 0) {
                        return (
                          <div className="vocab-examples">
                            <h4 className="vocab-examples-title">{t('examples.title')}</h4>
                            {dictExamples.slice(0, 3).map((ex, idx) => (
                              <div key={idx} className="vocab-example-item">
                                <div className="vocab-example-pos">{ex.pos} {ex.meaning}</div>
                                <div className="vocab-example-en">
                                  <ClickableText text={ex.example_en} />
                                </div>
                                <div className="vocab-example-zh">{ex.example_native}</div>
                              </div>
                            ))}
                          </div>
                        );
                      }

                      return (
                        <div className="vocab-examples">
                          <h4 className="vocab-examples-title">{t('examples.title')}</h4>
                          <p className="vocab-no-examples">{t('examples.noExamples')}</p>
                        </div>
                      );
                    })()}
                    <button
                      className="vocab-empty-btn"
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dictionary?word=${encodeURIComponent(item.vocabulary.word)}`);
                      }}
                    >
                      <Icons.ExternalLink />
                      {t('examples.viewFull')}
                    </button>
                  </div>
                )}

                {/* Card Footer */}
                <div className="vocab-card__footer">
                  <div className="vocab-mastery">
                    <div style={{ position: 'relative' }}>
                      <ProgressRing
                        level={item.masteryLevel}
                        onClick={() => setMasteryDropdownId(masteryDropdownId === item.id ? null : item.id)}
                        ariaLabel={t('card.masteryClick', { level: getMasteryLabel(item.masteryLevel) })}
                      />
                      {masteryDropdownId === item.id && (
                        <div className="mastery-dropdown" onClick={(e) => e.stopPropagation()}>
                          {[0, 1, 2, 3, 4, 5].map(level => (
                            <button
                              key={level}
                              className={`mastery-option ${item.masteryLevel === level ? 'selected' : ''}`}
                              onClick={() => handleUpdateMastery(item, level)}
                            >
                              <span
                                className="mastery-option-dot"
                                style={{ backgroundColor: getMasteryColor(level) }}
                              />
                              <span className="mastery-option-label">{getMasteryLabel(level)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mastery-info">
                      <span className="mastery-label">{t('mastery.title')}</span>
                      <span className="mastery-text">{getMasteryLabel(item.masteryLevel)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="vocab-date">
                      <Icons.Calendar />
                      {formatDate(item.createdAt)}
                    </span>
                    <button
                      className={`vocab-expand-btn ${isExpanded ? 'expanded' : ''}`}
                      onClick={(e) => toggleExpand(item.id, item.vocabulary.word, e)}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? t('actions.collapse') : t('actions.expand')}
                    >
                      {t('actions.expand')}
                      <Icons.ChevronDown />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        title={t('delete.title')}
        message={t('delete.message')}
        itemName={itemToDelete?.vocabulary.word || ''}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
      />

      <PracticeOptionsModal
        isOpen={practiceModalOpen}
        onClose={() => setPracticeModalOpen(false)}
        onStart={(options) => {
          setPracticeModalOpen(false);
          const params = new URLSearchParams();
          params.set('mode', options.mode);
          params.set('count', options.count.toString());
          if (options.mode === 'custom') {
            params.set('levels', options.masteryLevels.join(','));
          }
          if (options.favoritesOnly) {
            params.set('favorite', 'true');
          }
          if (options.learningModes && options.learningModes.length > 0) {
            params.set('stages', options.learningModes.join(','));
          }
          // Forward daily review IDs if present
          const vocabIds = searchParams.get('vocabIds');
          const kpIds = searchParams.get('kpIds');
          const patternIds = searchParams.get('patternIds');
          if (vocabIds) params.set('vocabIds', vocabIds);
          if (kpIds) params.set('kpIds', kpIds);
          if (patternIds) params.set('patternIds', patternIds);
          navigate(`/vocabulary-book/practice?${params.toString()}`);
        }}
        vocabularyList={vocabularyList}
      />
    </div>
  );
};

export default VocabularyBookPage;
